import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { Search, Menu, X, Heart, Sparkles } from 'lucide-react';
import { autocomplete } from '../api';
import { useWishlist } from '../context/WishlistContext';

const NAV_LINKS = [
  { label: 'Attività', href: '/attivita' },
  { label: 'Scopri', href: '/scopri' },
  { label: 'Categorie', href: '/attivita#categorie' },
];

export default function Header() {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [suggestions, setSuggestions] = useState(null);
  const [loading, setLoading] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [scrollProgress, setScrollProgress] = useState(0);
  const searchRef = useRef(null);
  const navigate = useNavigate();
  const location = useLocation();
  const debounceRef = useRef(null);
  const { count: wishlistCount } = useWishlist();

  useEffect(() => {
    const onScroll = () => {
      const y = window.scrollY;
      setScrolled(y > 12);
      const docH = document.documentElement.scrollHeight - window.innerHeight;
      setScrollProgress(docH > 0 ? (y / docH) * 100 : 0);
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => {
    if (query.length < 2) { setSuggestions(null); return; }
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(async () => {
      setLoading(true);
      try {
        const res = await autocomplete(query);
        setSuggestions(res.data);
      } catch {
        setSuggestions(null);
      } finally {
        setLoading(false);
      }
    }, 200);
  }, [query]);

  const handleSearch = (e) => {
    e.preventDefault();
    if (query.trim()) {
      navigate(`/attivita?q=${encodeURIComponent(query.trim())}`);
      setSearchOpen(false);
      setQuery('');
      setSuggestions(null);
    }
  };

  const hasSuggestions = suggestions && (
    suggestions.attivita?.length > 0 ||
    suggestions.categorie?.length > 0 ||
    suggestions.argomenti?.length > 0
  );

  const isActive = (href) => location.pathname === href || (href !== '/' && location.pathname.startsWith(href.split('#')[0]));

  return (
    <>
      {/* Scroll progress bar */}
      <div
        className="scroll-progress-bar"
        style={{ width: `${scrollProgress}%` }}
      />

      <header
        data-testid="site-header"
        className={`sticky top-0 z-50 border-b transition-all duration-300 ${scrolled ? 'glass' : ''}`}
        style={{
          backgroundColor: scrolled ? 'rgba(250,250,248,0.85)' : 'var(--mi-bg)',
          borderColor: 'var(--mi-border)',
        }}
      >
        <div className="mi-container">
          <div className="flex items-center justify-between h-16">
            {/* Logo */}
            <Link
              to="/"
              data-testid="site-logo-link"
              className="flex items-center gap-2 font-display text-xl tracking-tight transition-opacity hover:opacity-80"
              style={{ color: 'var(--mi-fg)' }}
            >
              <span className="text-2xl" style={{ color: 'var(--mi-accent)' }}>✦</span>
              <span className={`transition-all duration-300 ${scrolled ? 'text-lg' : 'text-xl'}`}>
                Mi-Ispira
              </span>
            </Link>

            {/* Desktop Nav */}
            <nav className="hidden md:flex items-center gap-1">
              {NAV_LINKS.map((l) => (
                <Link
                  key={l.label}
                  to={l.href}
                  data-testid={`header-nav-${l.label.toLowerCase().replace(/\s/g, '-')}-link`}
                  className={`accent-underline px-3 py-1.5 text-sm font-medium rounded-lg transition-colors hover:bg-[var(--mi-muted)] ${isActive(l.href) ? 'active' : ''}`}
                  style={{ color: isActive(l.href) ? 'var(--mi-fg)' : 'var(--mi-text)' }}
                >
                  {l.label === 'Scopri' ? (
                    <span className="flex items-center gap-1.5">
                      <Sparkles size={13} style={{ color: 'var(--mi-accent)' }} />
                      {l.label}
                    </span>
                  ) : l.label}
                </Link>
              ))}
            </nav>

            {/* Actions */}
            <div className="flex items-center gap-2">
              <button
                data-testid="header-search-open-button"
                onClick={() => setSearchOpen(!searchOpen)}
                className="hidden md:flex items-center gap-2 px-3 py-2 rounded-xl text-sm border transition-all hover:bg-white hover:border-[var(--mi-accent)] hover:shadow-sm"
                style={{ borderColor: 'var(--mi-border)', color: 'var(--mi-text)' }}
              >
                <Search size={14} />
                <span>Cerca...</span>
              </button>

              {/* Wishlist icon */}
              <Link
                to="/preferiti"
                className="relative flex items-center justify-center w-9 h-9 rounded-xl border transition-all hover:bg-white hover:border-[var(--mi-accent)]"
                style={{ borderColor: 'var(--mi-border)' }}
                title="Preferiti"
              >
                <Heart
                  size={16}
                  style={{ color: wishlistCount > 0 ? 'var(--mi-accent)' : 'var(--mi-text)' }}
                  fill={wishlistCount > 0 ? 'var(--mi-accent)' : 'none'}
                />
                {wishlistCount > 0 && (
                  <span
                    className="absolute -top-1.5 -right-1.5 w-4 h-4 rounded-full text-white flex items-center justify-center"
                    style={{ fontSize: '9px', backgroundColor: 'var(--mi-accent)', fontWeight: 700 }}
                  >
                    {wishlistCount > 9 ? '9+' : wishlistCount}
                  </span>
                )}
              </Link>

              <button
                className="md:hidden p-2 rounded-lg"
                onClick={() => setMobileOpen(!mobileOpen)}
                style={{ color: 'var(--mi-fg)' }}
              >
                {mobileOpen ? <X size={20} /> : <Menu size={20} />}
              </button>
            </div>
          </div>

          {/* Search bar (desktop dropdown) */}
          {searchOpen && (
            <div className="hidden md:block pb-4 relative animate-fade-in" ref={searchRef}>
              <form onSubmit={handleSearch}>
                <input
                  autoFocus
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  placeholder="Cerca attività, professionisti, categorie..."
                  className="w-full h-12 px-4 rounded-xl border text-sm transition-all"
                  style={{
                    borderColor: 'var(--mi-accent)',
                    backgroundColor: '#fff',
                    outline: 'none',
                    boxShadow: '0 0 0 3px var(--mi-accent-glow)',
                  }}
                />
              </form>
              {hasSuggestions && (
                <div
                  data-testid="autocomplete-popover"
                  className="absolute left-0 right-0 mt-2 rounded-xl border shadow-xl z-50 overflow-hidden animate-scale-in"
                  style={{ backgroundColor: '#fff', borderColor: 'var(--mi-border)' }}
                >
                  <SuggestionGroups
                    suggestions={suggestions}
                    onSelect={() => { setSearchOpen(false); setQuery(''); setSuggestions(null); }}
                    query={query}
                    navigate={navigate}
                  />
                </div>
              )}
            </div>
          )}

          {/* Mobile Nav */}
          {mobileOpen && (
            <div className="md:hidden border-t pb-4 pt-3 animate-fade-in" style={{ borderColor: 'var(--mi-border)' }}>
              <nav className="flex flex-col gap-1">
                {NAV_LINKS.map((l) => (
                  <Link
                    key={l.label}
                    to={l.href}
                    className="py-2 px-3 text-sm font-medium rounded-lg hover:bg-[var(--mi-muted)]"
                    style={{ color: 'var(--mi-text)' }}
                    onClick={() => setMobileOpen(false)}
                  >
                    {l.label}
                  </Link>
                ))}
                <Link
                  to="/preferiti"
                  className="py-2 px-3 text-sm font-medium rounded-lg hover:bg-[var(--mi-muted)] flex items-center gap-2"
                  style={{ color: 'var(--mi-text)' }}
                  onClick={() => setMobileOpen(false)}
                >
                  <Heart size={14} style={{ color: 'var(--mi-accent)' }} />
                  Preferiti {wishlistCount > 0 && `(${wishlistCount})`}
                </Link>
              </nav>
              <form onSubmit={handleSearch} className="mt-3">
                <input
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  placeholder="Cerca..."
                  className="w-full h-10 px-3 rounded-lg border text-sm"
                  style={{ borderColor: 'var(--mi-border)', backgroundColor: '#fff' }}
                />
              </form>
            </div>
          )}
        </div>
      </header>
    </>
  );
}

function SuggestionGroups({ suggestions, onSelect, query, navigate }) {
  const handleBiz   = (slug) => { navigate(`/attivita/${slug}`); onSelect(); };
  const handleCat   = (slug) => { navigate(`/categoria/${slug}`); onSelect(); };
  const handleTopic = (slug) => { navigate(`/argomento/${slug}`); onSelect(); };

  return (
    <div className="p-3 space-y-3">
      {suggestions.attivita?.length > 0 && (
        <div data-testid="autocomplete-group-attivita">
          <p className="text-xs uppercase tracking-widest mb-2 px-2" style={{ color: 'var(--mi-text)' }}>Attività</p>
          {suggestions.attivita.map((b) => (
            <button
              key={b.slug}
              onClick={() => handleBiz(b.slug)}
              className="flex items-center gap-3 w-full px-2 py-2 rounded-lg text-left transition-colors hover:bg-[var(--mi-muted)]"
            >
              {b.logo ? (
                <img src={b.logo} alt={b.nome} className="w-7 h-7 rounded-full object-cover border" style={{ borderColor: 'var(--mi-border)' }} />
              ) : (
                <div className="w-7 h-7 rounded-full flex items-center justify-center text-xs" style={{ backgroundColor: 'var(--mi-muted)', color: 'var(--mi-text)' }}>
                  {b.nome?.[0]}
                </div>
              )}
              <div>
                <p className="text-sm font-medium" style={{ color: 'var(--mi-fg)' }}>{b.nome}</p>
                <p className="text-xs" style={{ color: 'var(--mi-text)' }}>{b.categoria}{b.citta ? ` — ${b.citta}` : ''}</p>
              </div>
            </button>
          ))}
        </div>
      )}
      {suggestions.categorie?.length > 0 && (
        <div data-testid="autocomplete-group-categorie">
          <p className="text-xs uppercase tracking-widest mb-2 px-2" style={{ color: 'var(--mi-text)' }}>Categorie</p>
          {suggestions.categorie.map((c) => (
            <button
              key={c.slug}
              onClick={() => handleCat(c.slug)}
              className="flex items-center gap-2 w-full px-2 py-2 rounded-lg text-left transition-colors hover:bg-[var(--mi-muted)]"
            >
              <span className="text-sm" style={{ color: 'var(--mi-fg)' }}>{c.nome}</span>
              <span className="text-xs" style={{ color: 'var(--mi-text)' }}>({c.count})</span>
            </button>
          ))}
        </div>
      )}
      {suggestions.argomenti?.length > 0 && (
        <div data-testid="autocomplete-group-argomenti">
          <p className="text-xs uppercase tracking-widest mb-2 px-2" style={{ color: 'var(--mi-text)' }}>Argomenti</p>
          {suggestions.argomenti.map((t) => (
            <button
              key={t.slug}
              onClick={() => handleTopic(t.slug)}
              className="flex items-center gap-2 w-full px-2 py-2 rounded-lg text-left transition-colors hover:bg-[var(--mi-muted)]"
            >
              <span className="text-sm" style={{ color: 'var(--mi-fg)' }}>{t.nome}</span>
            </button>
          ))}
        </div>
      )}
      <div className="border-t pt-2 px-2" style={{ borderColor: 'var(--mi-border)' }}>
        <button
          onClick={() => { navigate(`/attivita?q=${encodeURIComponent(query)}`); onSelect(); }}
          className="text-xs font-medium hover:underline underline-offset-4"
          style={{ color: 'var(--mi-accent)' }}
        >
          Vedi tutti i risultati per "{query}" →
        </button>
      </div>
    </div>
  );
}
